import { DICE_COUNT, type HandState, type ScoredHand } from '../game/types';
import { rngFromString, type Rng } from '../game/rng';
import { canReroll, closeHand, reroll, rerollsLeft, rollHand, toggleHold } from '../game/hand';
import { scoreHand } from '../game/combos';
import { getLang, setLang, t } from '../lib/i18n';
import { sfxFanfare, sfxHold, sfxRattle, sfxScoreTick, sfxSettle } from './audio';

type Phase = 'idle' | 'rolling' | 'choice' | 'scored';

const BEST_KEY = 'tilt:best';
const ROLL_FLICKER_MS = 80;
const ROLL_BASE_MS = 420;
const ROLL_STAGGER_MS = 90;
const WINDUP_MS = 90;
const STAMP_MS = 120;

/**
 * Orchestrazione UI dello Sprint 0: una mano alla volta, gate "fa venire voglia di rilanciare?".
 * Tutta la casualità di gioco passa dall'rng seedato; Math.random qui sotto è SOLO cosmesi
 * (facce che sfarfallano durante il tumble).
 */
export class App {
  private phase: Phase = 'idle';
  private hand: HandState | null = null;
  private rng: Rng = rngFromString('boot');
  private handCounter = 0;
  private best = 0;
  /** Skip-on-tap: mai punire la fretta (art-direction §7). */
  private skipRequested = false;
  private detachSkip: (() => void) | null = null;

  private root: HTMLElement;
  private dice: HTMLButtonElement[] = [];
  private els!: {
    tagline: HTMLElement;
    lang: HTMLButtonElement;
    bestBox: HTMLElement;
    rolls: HTMLElement;
    preview: HTMLElement;
    scorePanel: HTMLElement;
    actions: HTMLElement;
    note: HTMLElement;
  };

  constructor(root: HTMLElement) {
    this.root = root;
    try {
      this.best = Number(localStorage.getItem(BEST_KEY)) || 0;
    } catch { /* best effort */ }
    this.build();
    this.renderTexts();
    this.renderActions();
  }

  private build(): void {
    this.root.innerHTML = `
      <header class="top">
        <div class="brand">
          <h1 class="logo">TILT</h1>
          <p class="tagline" data-el="tagline"></p>
        </div>
        <div class="top-right">
          <div class="best" data-el="best"></div>
          <button class="lang" data-el="lang" type="button"></button>
        </div>
      </header>
      <main class="table">
        <div class="dice" data-el="dice"></div>
        <div class="rolls" data-el="rolls"></div>
        <div class="preview" data-el="preview"></div>
        <div class="score-panel hidden" data-el="score"></div>
      </main>
      <footer class="bottom">
        <div class="actions" data-el="actions"></div>
        <p class="note" data-el="note"></p>
      </footer>
    `;
    const q = <T extends HTMLElement>(name: string): T => this.root.querySelector(`[data-el="${name}"]`) as T;
    this.els = {
      tagline: q('tagline'),
      lang: q<HTMLButtonElement>('lang'),
      bestBox: q('best'),
      rolls: q('rolls'),
      preview: q('preview'),
      scorePanel: q('score'),
      actions: q('actions'),
      note: q('note'),
    };

    const diceBox = q('dice');
    for (let i = 0; i < DICE_COUNT; i++) {
      const die = document.createElement('button');
      die.type = 'button';
      die.className = 'die empty';
      die.dataset['face'] = '1';
      die.setAttribute('aria-label', `die ${i + 1}`);
      for (let p = 0; p < 9; p++) die.appendChild(document.createElement('span'));
      die.addEventListener('click', () => this.onDieTap(i));
      diceBox.appendChild(die);
      this.dice.push(die);
    }

    this.els.lang.addEventListener('click', () => {
      setLang(getLang() === 'en' ? 'it' : 'en');
      this.renderTexts();
      this.renderActions();
      this.renderPreview();
    });
  }

  private renderTexts(): void {
    this.els.tagline.textContent = t('tagline');
    this.els.lang.textContent = getLang() === 'en' ? 'IT' : 'EN';
    this.els.bestBox.textContent = this.best > 0 ? `★ ${this.best}` : '';
    this.els.note.textContent = t('footer.sprint0');
  }

  private setFace(i: number, face: number): void {
    const die = this.dice[i];
    if (die) die.dataset['face'] = String(face);
  }

  private onDieTap(i: number): void {
    if (this.phase !== 'choice' || !this.hand) return;
    this.hand = toggleHold(this.hand, i);
    const held = this.hand.held[i] === true;
    this.dice[i]?.classList.toggle('held', held);
    sfxHold(held);
    this.renderActions();
  }

  private startHand(): void {
    this.handCounter += 1;
    // Seed per-mano: entropia UI, il motore resta puro (in Daily sarà il seed del giorno)
    this.rng = rngFromString(`free-${Date.now()}-${this.handCounter}`);
    this.hand = rollHand(this.rng);
    this.els.scorePanel.classList.add('hidden');
    this.els.preview.classList.remove('hidden');
    this.dice.forEach((d) => d.classList.remove('held', 'empty', 'used', 'dimmed'));
    this.animateRoll(this.hand.faces.map((_, i) => i));
  }

  private doReroll(): void {
    if (!this.hand || !canReroll(this.hand)) return;
    const before = this.hand.faces.slice();
    this.hand = reroll(this.hand, this.rng);
    const moving = this.hand.faces.map((_, i) => i).filter((i) => this.hand!.held[i] !== true);
    // ripristina visivamente le facce pre-rilancio sui dadi che si muovono
    moving.forEach((i) => this.setFace(i, before[i] ?? 1));
    this.animateRoll(moving);
  }

  /** Anticipazione → tumble → settle a cascata (art-direction §7), poi fase scelta. */
  private animateRoll(indices: number[]): void {
    if (!this.hand) return;
    this.phase = 'rolling';
    this.renderActions();
    this.renderRolls();
    this.els.preview.textContent = '';
    const finalFaces = this.hand.faces.slice();
    const totalMs = WINDUP_MS + ROLL_BASE_MS + indices.length * ROLL_STAGGER_MS;

    // anticipazione: senza carica non c'è colpo
    indices.forEach((i) => this.dice[i]?.classList.add('windup'));

    window.setTimeout(() => {
      sfxRattle(totalMs - WINDUP_MS);
      indices.forEach((i) => {
        const die = this.dice[i];
        die?.classList.remove('windup');
        die?.classList.add('rolling');
      });
      const flicker = window.setInterval(() => {
        for (const i of indices) {
          if (this.dice[i]?.classList.contains('rolling')) {
            this.setFace(i, 1 + Math.floor(Math.random() * 6));
          }
        }
      }, ROLL_FLICKER_MS);

      indices.forEach((i, order) => {
        window.setTimeout(() => {
          const die = this.dice[i];
          die?.classList.remove('rolling');
          die?.classList.add('settle');
          this.setFace(i, finalFaces[i] ?? 1);
          sfxSettle();
          window.setTimeout(() => die?.classList.remove('settle'), 240);
        }, ROLL_BASE_MS + order * ROLL_STAGGER_MS);
      });

      window.setTimeout(() => {
        window.clearInterval(flicker);
        this.phase = 'choice';
        this.renderActions();
        this.renderRolls();
        this.renderPreview();
      }, ROLL_BASE_MS + indices.length * ROLL_STAGGER_MS + 120);
    }, WINDUP_MS);
  }

  private renderRolls(): void {
    if (!this.hand || this.phase === 'idle') {
      this.els.rolls.textContent = '';
      return;
    }
    const left = rerollsLeft(this.hand);
    const dots = Array.from({ length: 2 }, (_, i) =>
      `<span class="dot ${i < left ? 'full' : 'spent'}"></span>`).join('');
    this.els.rolls.innerHTML = `${dots} <span>${t('hint.rollsLeft')}</span>`;
  }

  private renderPreview(): void {
    if (!this.hand || this.phase !== 'choice') return;
    const s = scoreHand(this.hand.faces);
    this.els.preview.innerHTML =
      `<strong>${t(`combo.${s.best.id}`)}</strong>` +
      ` <span class="formula">${s.best.base} + ${s.chips - s.best.base} = ${s.chips} × ${s.mult}</span>`;
  }

  private bank(): void {
    if (!this.hand || this.phase !== 'choice') return;
    const { state, result } = closeHand(this.hand);
    this.hand = state;
    this.phase = 'scored';
    this.renderActions();
    this.els.preview.classList.add('hidden');
    // la combo è un faro, il resto è comparse
    this.dice.forEach((d, i) => {
      const used = result.best.usedIndices.includes(i);
      d.classList.toggle('used', used);
      d.classList.toggle('dimmed', !used);
    });
    this.showScore(result);
  }

  /**
   * Coreografia del punteggio (art-direction §7, ordine esatto):
   * combo pop + fanfara → chips count → il mult TIMBRA → totale che macina (lo shake parte qui).
   * Un tap in qualunque momento salta al totale finale.
   */
  private showScore(result: ScoredHand): void {
    const panel = this.els.scorePanel;
    panel.classList.remove('hidden');
    panel.innerHTML = `
      <div class="combo-name pop">${t(`combo.${result.best.id}`)}</div>
      <div class="breakdown">
        <span>${t('score.chips')} <b data-el="chips">0</b></span>
        <span class="x">×</span>
        <span>${t('score.mult')} <b class="mult-value" data-el="mult">${result.mult}</b></span>
      </div>
      <div class="total" data-el="total">0</div>
    `;
    const level: 1 | 2 | 3 = result.best.base >= 120 ? 3 : result.best.base >= 60 ? 2 : 1;
    sfxFanfare(level);
    this.beginSkippable();

    const chipsEl = panel.querySelector('[data-el="chips"]') as HTMLElement;
    const multEl = panel.querySelector('[data-el="mult"]') as HTMLElement;
    const totalEl = panel.querySelector('[data-el="total"]') as HTMLElement;
    if (level >= 2) totalEl.classList.add('glow');

    this.countUp(chipsEl, result.chips, 450, () => {
      // il mult non conta: timbra
      multEl.classList.add('stamp');
      sfxSettle();
      this.delay(STAMP_MS, () => {
        // lo shake celebra il numero, non la combo
        if (level === 3 && !this.skipRequested) {
          document.body.classList.add('shake');
          window.setTimeout(() => document.body.classList.remove('shake'), 500);
        }
        this.countUp(totalEl, result.score, 650, () => {
          this.endSkippable();
          if (result.score > this.best) {
            this.best = result.score;
            try {
              localStorage.setItem(BEST_KEY, String(this.best));
            } catch { /* best effort */ }
            totalEl.classList.add('new-best');
            this.renderTexts();
          }
        });
      });
    });
  }

  private beginSkippable(): void {
    this.skipRequested = false;
    const onTap = (): void => {
      this.skipRequested = true;
    };
    document.addEventListener('pointerdown', onTap, { once: true });
    this.detachSkip = () => document.removeEventListener('pointerdown', onTap);
  }

  private endSkippable(): void {
    this.detachSkip?.();
    this.detachSkip = null;
    this.skipRequested = false;
  }

  /** setTimeout che collassa a immediato se l'utente ha chiesto di saltare. */
  private delay(ms: number, cb: () => void): void {
    if (this.skipRequested) {
      cb();
      return;
    }
    window.setTimeout(cb, ms);
  }

  /** Il contatore che macina numeri — metà della pornografia numerica sta qui. Skippabile col tap. */
  private countUp(el: HTMLElement, target: number, durMs: number, onDone?: () => void): void {
    const start = performance.now();
    let lastTick = 0;
    const step = (now: number): void => {
      const p = this.skipRequested ? 1 : Math.min(1, (now - start) / durMs);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = String(Math.round(target * eased));
      if (now - lastTick > 55 && p < 1) {
        lastTick = now;
        sfxScoreTick(p);
      }
      if (p < 1) requestAnimationFrame(step);
      else onDone?.();
    };
    requestAnimationFrame(step);
  }

  private renderActions(): void {
    const box = this.els.actions;
    box.innerHTML = '';
    const button = (label: string, cls: string, onTap: () => void, disabled = false): void => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `btn ${cls}`;
      b.textContent = label;
      b.disabled = disabled;
      b.addEventListener('click', onTap);
      box.appendChild(b);
    };

    if (this.phase === 'idle') {
      button(t('action.roll'), 'primary big', () => this.startHand());
      return;
    }
    if (this.phase === 'rolling') {
      button('…', 'primary big', () => undefined, true);
      return;
    }
    if (this.phase === 'scored') {
      button(t('action.again'), 'primary big', () => this.startHand());
      return;
    }
    // choice
    const hand = this.hand;
    const left = hand ? rerollsLeft(hand) : 0;
    button(
      t('action.reroll', { n: left }),
      'secondary',
      () => this.doReroll(),
      !hand || !canReroll(hand),
    );
    button(t('action.bank'), 'primary', () => this.bank());
    this.els.note.textContent = t('hint.hold');
  }
}
